import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMatErrorsModule } from 'ngx-mat-errors';
import { finalize, catchError, of, map, Observable } from 'rxjs';
import { EnterpriseApiService } from 'app/modules/enterprise/enterprise.api.service';
import { UsersApiService } from 'app/modules/admin/users/users.api.service';

export type EmailCheckApiType = 'enterprise' | 'users';

export enum EmailCheckStatus {
    NOT_FOUND = 'NOT_FOUND',
    EXISTS_SAME_CLIENT = 'EXISTS_SAME_CLIENT',
    EXISTS_DIFFERENT_CLIENT = 'EXISTS_DIFFERENT_CLIENT'
}

export interface IEmailCheckUser {
    id: string;
    name: string;
    email: string;
    cpf?: string;
    phoneNumber?: string;
}

export interface IEmailCheckResult {
    status: EmailCheckStatus;
    email: string;
    user?: IEmailCheckUser;
}

@Component({
    selector: 'app-email-check',
    templateUrl: './email-check.component.html',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
        NgxMatErrorsModule
    ]
})
export class EmailCheckComponent implements OnInit {
    private readonly _fb = inject(FormBuilder);
    private readonly _enterpriseApiService = inject(EnterpriseApiService);
    private readonly _usersApiService = inject(UsersApiService);
    private readonly _destroyRef = inject(DestroyRef);

    @Input() apiType: EmailCheckApiType = 'enterprise';
    @Input() applicationId = '';
    @Input() title = 'Verificar Email';
    @Input() description = 'Informe o email do usuário para verificar se já existe no sistema.';
    @Input() showCancelButton = true;
    @Input() verifyButtonText = 'Verificar Email';
    
    @Output() emailVerified = new EventEmitter<IEmailCheckResult>();
    @Output() cancelled = new EventEmitter<void>();

    readonly EmailCheckStatus = EmailCheckStatus;

    emailForm: FormGroup;
    isCheckingEmail = signal<boolean>(false);
    emailCheckStatus = signal<EmailCheckStatus | null>(null);
    existingUser = signal<IEmailCheckUser | null>(null);

    readonly isVerified = computed(() => {
        const status = this.emailCheckStatus();
        return status === EmailCheckStatus.NOT_FOUND || status === EmailCheckStatus.EXISTS_SAME_CLIENT;
    });

    readonly errorMessage = computed(() => 
        this.emailCheckStatus() === EmailCheckStatus.EXISTS_DIFFERENT_CLIENT 
            ? 'Este usuário já existe em outra empresa e você não tem acesso a ele.' 
            : null
    );

    ngOnInit(): void {
        this.emailForm = this._fb.group({
            email: new FormControl('', [Validators.required, Validators.email])
        });
    }

    onCheckEmail(): void {
        if (this.emailForm.invalid) {
            this.emailForm.markAllAsTouched();
            return;
        }

        const email = this.emailForm.get('email')?.value as string;
        this._performEmailCheck(email);
    }

    onCancelClick(): void {
        this.cancelled.emit();
    }

    onProceedClick(): void {
        const email = this.emailForm.get('email')?.value;
        const result: IEmailCheckResult = {
            status: this.emailCheckStatus(),
            email,
            user: this.existingUser() || undefined
        };
        this.emailVerified.emit(result);
    }

    reset(): void {
        this.emailForm.reset();
        this.emailCheckStatus.set(null);
        this.existingUser.set(null);
    }

    private _performEmailCheck(email: string): void {
        this.isCheckingEmail.set(true);
        this.emailCheckStatus.set(null);
        this.existingUser.set(null);

        this._getEmailCheckObservable(email).pipe(
            finalize(() => this.isCheckingEmail.set(false)),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe({
            next: (response: any) => {
                this.emailCheckStatus.set(response.status);
                if (response.user) {
                    this.existingUser.set(response.user);
                }
            }
        });
    }

    private _getEmailCheckObservable(email: string): Observable<any> {
        if (this.apiType === 'users') {
            return this._usersApiService.getUsers({ searchQuery: email, pageNumber: 1, pageSize: 1 }).pipe(
                map((response: any) => {
                    const user = response.data?.find((u: any) => u.email?.toLowerCase() === email.toLowerCase());
                    if (user) {
                        return {
                            status: EmailCheckStatus.EXISTS_SAME_CLIENT,
                            user: {
                                id: user.id,
                                name: user.name,
                                email: user.email,
                                cpf: user.cpf,
                                phoneNumber: user.phoneNumber
                            } as IEmailCheckUser
                        };
                    }
                    return { status: EmailCheckStatus.NOT_FOUND };
                }),
                catchError((error: any) => {
                    if (error?.status === 403 || error?.status === 401) {
                        return of({ status: EmailCheckStatus.EXISTS_DIFFERENT_CLIENT });
                    }
                    return of({ status: EmailCheckStatus.NOT_FOUND });
                })
            );
        }

        return this._enterpriseApiService.getUserDetail({ email }).pipe(
            map((response: any) => ({
                status: EmailCheckStatus.EXISTS_SAME_CLIENT,
                user: {
                    id: response.id,
                    name: response.name,
                    email: response.email,
                    cpf: response.cpf,
                    phoneNumber: response.phoneNumber
                } as IEmailCheckUser
            })),
            catchError((error: any) => {
                if (error?.status === 403 || error?.status === 401) {
                    return of({ status: EmailCheckStatus.EXISTS_DIFFERENT_CLIENT });
                }
                return of({ status: EmailCheckStatus.NOT_FOUND });
            })
        );
    }
}

